// AddKitchen.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Container,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import KitchenDetails from "./KitchenDetails";
import KitchenWelcome from "./KitchenWelcome";
import KitchenLocation from "./KitchenLocation";
import KitchenPrice from "./KitchenPrice";
import "./AddKitchen.css";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../config";
import KitchenImages from "./KitchenImages";
import KitchenDocuments from "./KitchenDocuments";
import KitchenAmenities from "./KitchenAmenities";
import KitchenReview from "./KitchenReview";
import { ToastContainer, toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import KitchenOwnerTermsModal from "../modals/modalVariants/KitchenOwnerTermsModal";

import AuthModal from "../modals/AuthModal/AuthModal";

// Add other imports...

const AddKitchen = ({ editMode }) => {
  const { kitchenID } = useParams();
  const navigate = useNavigate();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const totalSteps = 8;
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const pendingSubmission = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openTermsModal = () => setShowTermsModal(true);
  const closeTermsModal = () => setShowTermsModal(false);

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    if (pendingSubmission.current && localStorage.getItem("token")) {
      handleSubmit(pendingSubmission.current);
      pendingSubmission.current = null;
    }
  };

  const handleAgreeToTerms = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      pendingAgreeRef.current = true;
      openAuthModal("login");
    } else {
      updateKitchenData({ agreed: true });
      showToast("You agreed to the Terms.", "success");
    }
    setShowTermsModal(false);
  };

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, { icon: "✅" });
    } else if (type === "error") {
      toast.error(message);
    } else if (type === "info") {
      toast.info(message);
    }
  };
  const userIDSet = useRef(false);
  const [kitchenData, setKitchenData] = useState({
    title: "",
    category: "",
    size: "",
    city: "",
    neighborhood: "",
    description: "",
    checkinmessage: "",
    agreed: false,
    bookingType: "hour",
    hourlyRate: "",
    dayRate: "",
    cleaningFee: "",
    minBooking: "",
    securityDeposit: "",
    rateEntries: [],
    images: [],
    address1: "",
    address2: "",
    city1: "",
    state: "",
    zipcode: "",
    country: "",
    address: "",
    latitude: null,
    longitude: null,
    amenities: {},
    documents: [],
    timeZone: "",
  });

  useEffect(() => {
    localStorage.setItem("kitchenInProgress", JSON.stringify(kitchenData));
  }, [kitchenData]);

  const updateKitchenData = useCallback((newData) => {
    setKitchenData((prev) => ({ ...prev, ...newData }));
  }, []);

  const validateCurrentStep = () => {
    if (stepIndex === 1) {
      const titleMissing = !kitchenData.title;
      const termsNotAgreed = !kitchenData.agreed;

      if (titleMissing || termsNotAgreed) {
        if (titleMissing && termsNotAgreed) {
          showToast(
            "Please fill out Kitchen Title and agree to the Terms.",
            "error"
          );
        } else if (titleMissing) {
          showToast("Please fill out Kitchen Title.", "error");
        } else if (termsNotAgreed) {
          showToast("You must agree to the Terms.", "error");
        }
        return false;
      }
    }

    // if (stepIndex === 2 && (!kitchenData.address || !kitchenData.latitude)) {
    //   showToast("Please provide a valid kitchen address.", "error");
    //   return false;
    // }

    // if (stepIndex === 3 && (!kitchenData.address || !kitchenData.latitude)) {
    //   showToast("Please provide a valid kitchen address.", "error");
    //   return false;
    // }

    return true;
  };

  const goNext = () => {
    if (!validateCurrentStep()) return;
    if (stepIndex < totalSteps - 1) {
      setStepIndex((prev) => prev + 1);
    } else {
      handleSubmit(); // final submit
    }
  };

  const goBack = () => {
    if (stepIndex > 0) {
      setStepIndex((prev) => prev - 1);
    }
  };

  const progress = ((stepIndex + 1) / totalSteps) * 100;

  const handleSubmit = async (e) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        pendingSubmission.current = e;
        openAuthModal("login");
        setIsSubmitting(false);
        return;
      }

      if (editMode) {
        await axios.put(
          `${config.apiBaseUrl}/api/kitchens/edit/${kitchenData.KitchenID}`,
          kitchenData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success("Kitchen successfully updated!", { icon: "✅" });

        // Wait a little before navigating
        setTimeout(() => {
          navigate("/dashboard/my-listings");
        }, 1000);
      } else {
        await axios.post(`${config.apiBaseUrl}/api/kitchens/add`, kitchenData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        toast.success("Kitchen saved successfully!", { icon: "✅" });

        setTimeout(() => {
          navigate("/dashboard/my-listings");
        }, 1000);
      }
    } catch (error) {
      console.error("Error submitting kitchen data:", error.message);
      toast.error("Failed to save kitchen data. Please try again.");
    } finally {
      setIsSubmitting(false); // Allow future submits
    }
  };

  useEffect(() => {
    if (!editMode) {
      setStepIndex(0);
    } else {
      setStepIndex(7);
    }
  }, [editMode]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIndex]);

  useEffect(() => {
    if (editMode && kitchenID) {
      const fetchKitchenData = async () => {
        try {
          const response = await axios.get(
            `${config.apiBaseUrl}/api/kitchens/kitchen/${kitchenID}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const fetchedData = {
            KitchenID: response.data.KitchenID,
            title: response.data.Title,
            category: response.data.Category,
            size: response.data.Size,
            city: response.data.City,
            neighborhood: response.data.Neighborhood,
            description: response.data.Description,
            checkinmessage: response.data.CheckinMessage,
            agreed: response.data.Agreed,
            bookingType: response.data.BookingType,
            hourlyRate: response.data.HourlyRate,
            dayRate: response.data.DayRate,
            cleaningFee: response.data.CleaningFee,
            minBooking: response.data.MinBooking,
            securityDeposit: response.data.SecurityDeposit,
            rateEntries: response.data.RateEntries || [],
            images: response.data.Images || [],
            addressLine1: response.data.addressLine1 || [],
            addressLine2: response.data.addressLine2 || [],
            city: response.data.city || [],
            state: response.data.state || [],
            zipcode: response.data.zipcode || [],
            country: response.data.country || [],
            address: response.data.Address,
            latitude: response.data.Latitude,
            longitude: response.data.Longitude,
            amenities: response.data.Amenities || {},
            documents: response.data.Documents || [],
            timeZone: response.data.timeZone || "",
          };
          setKitchenData(fetchedData);
        } catch (error) {
          console.error("Error fetching kitchen data:", error);
        }
      };
      fetchKitchenData();
    }
  }, [editMode, kitchenID]);

  useEffect(() => {
    if (!userIDSet.current) {
      const userID = localStorage.getItem("userID");
      if (userID && !kitchenData.UserID) {
        updateKitchenData({ UserID: userID });
        userIDSet.current = true;
      }
    }
  }, [updateKitchenData, kitchenData.UserID]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "95vh",
        paddingBottom: { xs: "30px" },
        backgroundColor: "white",
        position: "relative",
      }}
    >
      <IconButton
        onClick={() => setShowExitConfirm(true)}
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 120,
          backgroundColor: "rgba(255,255,255,0.85)",
          boxShadow: 1,
        }}
      >
        <CloseIcon />
      </IconButton>

      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          py: 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {stepIndex !== 0 && (
          <Typography variant="h5" align="center" gutterBottom>
            {editMode ? "Edit Kitchen" : "Add New Kitchen"}
          </Typography>
        )}
        <ToastContainer
          position="top-right"
          pauseOnHover={true}
          autoClose={2000}
          style={{ zIndex: 99999999, position: "fixed", top: "60px" }}
        />

        {!editMode && stepIndex !== 0 && (
          <LinearProgress
            variant="determinate"
            sx={{
              height: 5,
              borderRadius: 5,
              backgroundColor: "#bfc5ca",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#2c3e50",
              },
              mb: 3,
            }}
            value={progress}
          />
        )}
        <Grow in={true} timeout={500} key={stepIndex}>
          <Box
            sx={{
              width: "100%",
              bgcolor: "white",
              p: { xs: 2, sm: 3, md: 4 },
              flexGrow: 1,
              minHeight: "60vh",
              overflowY: "auto",
            }}
          >
            {/* Render Step Components */}
            {stepIndex === 0 && (
              <KitchenWelcome onNext={() => setStepIndex(1)} />
            )}
            {stepIndex === 1 && (
              <KitchenDetails
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
                openTermsModal={() => setShowTermsModal(true)}
              />
            )}
            {stepIndex === 2 && (
              <KitchenLocation
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            )}
            {stepIndex === 3 && (
              <KitchenPrice
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            )}

            {stepIndex === 4 && (
              <KitchenImages
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            )}

            {stepIndex === 5 && (
              <KitchenDocuments
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            )}

            {stepIndex === 6 && (
              <KitchenAmenities
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            )}
            {stepIndex === 7 && (
              <KitchenReview
                kitchenData={kitchenData}
                handleEdit={(stepNum) => setStepIndex(stepNum)}
              />
            )}
          </Box>
        </Grow>

        <KitchenOwnerTermsModal
          open={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          onAgree={handleAgreeToTerms}
        />
        <AuthModal
          open={showAuthModal}
          onClose={closeAuthModal}
          initialMode={authMode}
        />
        {/* Navigation */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            borderTop: "1px solid #e0e0e0",
            paddingTop: "20px",
            paddingBottom: "20px",
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          {!editMode && stepIndex > 0 ? (
            <Button className="bb1" onClick={goBack}>
              Back
            </Button>
          ) : (
            <Box /> // this keeps spacing aligned when Back is hidden
          )}
          {stepIndex !== 0 && (
            <Button
              variant="contained"
              className="nb1"
              onClick={() => {
                if (editMode && stepIndex === 7) {
                  handleSubmit();
                  return;
                }

                if (editMode) {
                  const isValid = validateCurrentStep();
                  if (!isValid) return;
                  setStepIndex(7);
                  return;
                }

                goNext();
              }}
            >
              {editMode && stepIndex === 7
                ? "Update Kitchen"
                : editMode
                ? "Save"
                : stepIndex === totalSteps - 1
                ? "Submit for Review"
                : "Next"}
            </Button>
          )}
        </Box>
      </Container>

      <Dialog
        open={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "18px", pb: 0 }}>
          Are you sure you want to exit?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your progress up until now has been saved.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 3,
            pb: 2,
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#2c3e50", // match your brand color
              "&:hover": {
                backgroundColor: "#1a252f",
              },
            }}
            onClick={() => navigate("/dashboard/my-listings")}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowExitConfirm(false)}
            sx={{
              backgroundColor: "#2c3e50", // match your brand color
              "&:hover": {
                backgroundColor: "#1a252f",
              },
            }}
          >
            Keep working
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default AddKitchen;
